import React from "react";
import {
  FiMapPin,
  FiShield,
  FiTruck,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const heroHighlights = [
  {
    icon: FiTruck,
    title: "Fast & Reliable",
    description: "Deliveries on time, every time.",
  },
  {
    icon: FiShield,
    title: "Safe & Secure",
    description: "Your packages are in safe hands.",
  },
  {
    icon: FiMapPin,
    title: "Wide Coverage",
    description: "Pan-India reach you can count on.",
  },
  {
    icon: FiUserCheck,
    title: "Customer First",
    description: "Support that always cares.",
  },
];

const valueCards = [
  {
    icon: FiTruck,
    title: "Fast & Reliable",
    description:
      "We ensure on-time deliveries with a professional fleet designed to move at the speed of your business.",
  },
  {
    icon: FiUsers,
    title: "Customer First",
    description:
      "Our services are built around customer convenience, flexibility, and satisfaction at every step.",
  },
  {
    icon: FiShield,
    title: "Premium Quality",
    description:
      "We go above and beyond to deliver high-quality logistics solutions with care and professionalism.",
  },
  {
    icon: FiMapPin,
    title: "Sustainable Impact",
    description:
      "We're committed to responsible logistics that reduce impact and create a better future for everyone.",
  },
];

const AboutPage = () => {
  return (
    <section className="min-h-screen w-screen overflow-x-hidden bg-[linear-gradient(180deg,#f7f2e7_0%,#fffdfa_26%,#fffaf1_100%)] text-[#141b2d]">
      <div className="flex min-h-screen w-full flex-col px-0 pb-0 pt-3 sm:pt-4">
        <Navbar />

        <div className="mt-3 w-full overflow-hidden rounded-[32px] bg-[#fffdf8] shadow-[0_24px_70px_rgba(177,146,90,0.14)]">
          <section className="relative overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, rgba(7,14,22,0.96) 0%, rgba(8,15,24,0.92) 26%, rgba(11,18,28,0.72) 44%, rgba(16,25,35,0.30) 62%, rgba(16,25,35,0.12) 78%, rgba(16,25,35,0.08) 100%), url('/assets/delivery_man_images/truck_img.png')",
              }}
            />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_68%_12%,rgba(255,193,56,0.28),rgba(255,255,255,0)_18%),linear-gradient(180deg,rgba(255,193,56,0.03)_0%,rgba(12,17,23,0.08)_100%)]" />

            <div className="relative px-8 pb-10 pt-12 sm:px-10 lg:px-12 lg:pt-14">
              <div className="max-w-[620px] pt-10">
                <h1 className="text-[38px] font-extrabold leading-[0.98] tracking-[-0.05em] text-white sm:text-[56px] lg:text-[74px]">
                  We are building the
                  <span className="block text-[#ffc52a]">delivery network</span>
                  <span className="block">for commerce</span>
                </h1>

                <p className="mt-6 max-w-[560px] text-[16px] leading-[1.8] text-white sm:text-[18px] lg:mt-8 lg:text-[21px]">
                  Trusted partner for individuals, shops, e-commerce sellers and
                  businesses delivering speed, reliability and trust.
                </p>
              </div>

              <div className="mt-10 grid gap-5 rounded-[22px] border border-white/20 bg-[rgba(8,14,22,0.64)] px-5 py-6 backdrop-blur-[6px] sm:grid-cols-2 lg:mt-12 lg:grid-cols-4 lg:gap-6 lg:px-6 lg:py-7">
                {heroHighlights.map(({ icon: Icon, title, description }, index) => (
                  <div
                    key={title}
                    className={`flex items-start gap-4 ${
                      index !== heroHighlights.length - 1
                        ? "lg:border-r lg:border-white/20 lg:pr-5"
                        : ""
                    }`}
                  >
                    <div className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-[#0d5d2b] shadow-sm">
                      <Icon className="text-[22px]" />
                    </div>
                    <div>
                      <h3 className="text-[22px] font-semibold text-white">{title}</h3>
                      <p className="mt-2 text-[18px] font-medium leading-[1.6] text-white/95 [text-shadow:0_1px_8px_rgba(0,0,0,0.42)]">
                        {description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-8 pb-0 pt-10 sm:px-10 lg:px-12">
            <div className="grid items-center gap-10 lg:grid-cols-[46%_54%]">
              <div className="relative">
                <div className="absolute -bottom-5 left-0 z-20 flex items-center gap-4 rounded-[24px] bg-[linear-gradient(180deg,#0f5a29_0%,#083f1d_100%)] px-8 py-6 text-white shadow-[0_20px_40px_rgba(9,66,31,0.24)]">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-[rgba(255,255,255,0.08)]">
                    <FiTruck className="text-[28px]" />
                  </div>
                  <div>
                    <p className="text-[38px] font-extrabold leading-none">10,000+</p>
                    <p className="mt-2 text-[20px] text-white/88">Deliveries Completed</p>
                  </div>
                </div>
                <img
                  src="/assets/delivery_man_images/abt_1.png"
                  alt="Instaport delivery executive with parcel"
                  className="h-[360px] w-full rounded-[30px] object-cover object-[center_14%] shadow-[0_18px_42px_rgba(177,146,90,0.16)] sm:h-[440px] lg:h-[520px] lg:object-center"
                />
              </div>

              <div className="relative px-4">
                <div className="absolute right-0 top-0 grid grid-cols-4 gap-3 opacity-55">
                  {Array.from({ length: 16 }).map((_, index) => (
                    <span
                      key={index}
                      className="h-[4px] w-[4px] rounded-full bg-[#ebcb83]"
                    />
                  ))}
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-[18px] font-bold uppercase tracking-[0.22em] text-[#24592e]">
                    Who We Are
                  </span>
                  <span className="h-[3px] w-16 rounded-full bg-[#efb01b]" />
                </div>

                <h2 className="mt-7 text-[36px] font-extrabold leading-[1.05] tracking-[-0.045em] text-[#141b2d] sm:text-[48px] lg:text-[62px]">
                  Built for Speed.
                  <span className="block">Driven by Trust.</span>
                </h2>

                <div className="mt-8 space-y-6 text-[16px] leading-[1.85] text-[#4d5669] sm:text-[18px] lg:space-y-8 lg:text-[20px]">
                  <p>
                    Instaport delivers value through premium same-day delivery
                    services. We are a leading B2B and B2C logistics platform
                    designed around customer convenience.
                  </p>
                  <p>
                    Our mission is to create the world's best end-to-end delivery
                    ecosystem with efficiency, reliability and innovation at its
                    core.
                  </p>
                  <p>
                    With a team of quality-rated and professional delivery
                    executives, we strive to build strong relationships and serve
                    as a helping hand for businesses and individuals alike.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="px-8 pb-0 pt-14 sm:px-10 lg:px-12">
            <div className="rounded-[34px] border border-[#edf0ea] bg-[linear-gradient(180deg,#fcfdfc_0%,#f9fbf9_100%)] px-6 pb-8 pt-8 shadow-[0_18px_45px_rgba(191,166,116,0.10)]">
              <div className="text-center">
                <p className="text-[18px] font-bold uppercase tracking-[0.2em] text-[#24592e]">
                  Our Core Values
                </p>
                <h2 className="mt-4 text-[34px] font-extrabold tracking-[-0.04em] text-[#171f31] sm:text-[46px] lg:text-[54px]">
                  Delivering More Than Packages
                </h2>
              </div>

              <div className="mt-10 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
                {valueCards.map(({ icon: Icon, title, description }) => (
                  <div
                    key={title}
                    className="rounded-[28px] border border-[#ebeee7] bg-white px-8 py-9 text-center shadow-[0_16px_36px_rgba(191,166,116,0.10)]"
                  >
                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#eef5eb] text-[#184f25]">
                      <Icon className="text-[34px]" />
                    </div>
                    <h3 className="mt-8 text-[28px] font-semibold text-[#1a2233]">
                      {title}
                    </h3>
                    <div className="mx-auto mt-4 h-[3px] w-12 rounded-full bg-[#f0b322]" />
                    <p className="mt-6 text-[18px] leading-[1.8] text-[#5b6577]">
                      {description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-8 pb-0 pt-8 sm:px-10 lg:px-12">
            <div className="grid overflow-hidden rounded-[34px] bg-[linear-gradient(90deg,#06381a_0%,#0b4f26_56%,#0d582c_100%)] shadow-[0_18px_42px_rgba(6,56,26,0.24)] lg:grid-cols-[46%_54%]">
              <div className="px-8 py-10 text-white">
                <p className="text-[20px] font-bold uppercase tracking-[0.18em] text-[#ffc52a]">
                  Join The Instaport Journey
                </p>
                <h2 className="mt-5 text-[38px] font-extrabold leading-[1.06] tracking-[-0.045em] sm:text-[50px] lg:text-[64px]">
                  Explore a career
                  <span className="block">
                    with <span className="text-[#ffc52a]">Instaport</span>
                  </span>
                </h2>
                <p className="mt-6 max-w-[560px] text-[16px] leading-[1.8] text-white/88 sm:text-[18px] lg:mt-8 lg:text-[20px]">
                  We're looking for curious, passionate people with a desire to
                  solve challenging business problems. We move fast, operate
                  under pressure and with limited resources. If you're ready to
                  make an impact, we'd love to hear from you.
                </p>
              </div>

              <div className="relative px-5 py-5">
                <img
                  src="/assets/delivery_man_images/abt_2.png"
                  alt="Instaport delivery team"
                  className="h-[300px] w-full rounded-[28px] object-cover object-[center_22%] sm:h-[380px] lg:h-[440px] lg:object-center"
                />
              </div>
            </div>
          </section>

          <div className="mt-8 overflow-hidden rounded-t-[32px] bg-white shadow-[0_-10px_30px_rgba(177,146,90,0.05)]">
            <Footer />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutPage;
